import { createContext, useRef, PropsWithChildren } from 'react'
import { AxiosResponse } from 'axios'
import { Toast, ToastMessage } from 'primereact/toast'

import { useProvideContext } from '../../hooks/context'
import { pi } from '../common/pure/PrimeIcon'
import { ApiError } from '../../models/ResponseError'
import { ok } from '../../tools/api'
import { isDevEnv } from '../../tools/env'
import { capitalize, pastTensed } from '../../tools/grammar'

declare type ToastFunction = (title: string, text: string) => void

export interface ToastContextValue {
    success: ToastFunction
    danger: ToastFunction
    info: ToastFunction
    completed: (action: string, subject: string, pastTense?: string) => void
    error: (error: Error, title?: string) => void
    response: (response: AxiosResponse) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

/**
 * This context provides a single Toast component, which can be used to display any number of toasts for any purpose.
 */
export default function ({ children }: PropsWithChildren) {
    const toast = useRef(null)

    const post = (message: ToastMessage) => {
        message = {
            ...message,
            id: Date.now().toString(),
        }
        const current = toast.current as Toast | null
        current?.show(message)
    }

    const danger = (summary: string, detail?: string) =>
        post({
            severity: 'error',
            icon: pi('exclamation-triangle'),
            summary,
            detail,
        })

    const success = (summary: string, detail?: string) =>
        post({
            severity: 'success',
            icon: pi('check'),
            summary,
            detail,
        })

    const info = (summary: string, detail?: string) =>
        post({
            severity: 'info',
            icon: pi('info'),
            summary,
            detail,
        })

    const completed = (action: string, subject: string, pastTense: string = pastTensed(action)) =>
        success(
            capitalize(action) + ' completed',
            `${capitalize(subject)} ${pastTense} successfully!`
        )

    const error = (object: Error = new Error('Unknown error'), title?: string) =>
        danger(title ?? Object.getPrototypeOf(object).constructor.name, object.message)

    /**
     * Toast the result of an http response.
     * Because it is a bit of an anti-pattern, the approach should be used sparingly - if at all.
     * Feel free to use liberally for debugging!
     */
    const response = (object: AxiosResponse) => {
        const method = object.config.method?.toUpperCase()
        const data = object.data as ApiError
        if (isDevEnv()) {
            if (ok(object)) success(`${method} success`, JSON.stringify(data, null, 4))
            else danger(`${method} error`, JSON.stringify(data, null, 4))
        } else {
            if (ok(object)) success(`${method} success`, data.title)
            else danger(`${method} error`, data.title)
        }
    }

    const value: ToastContextValue = {
        danger,
        success,
        info,
        completed,
        error,
        response,
    }

    return (
        <ToastContext.Provider value={value}>
            {children}
            <Toast ref={toast} position="bottom-right" />
        </ToastContext.Provider>
    )
}

/**
 * Functions in this namespace have generic names and may overlap with many other names.
 * Instead of employing destructuring as usual, please use the context as a named variable.
 *
 * @example
 * Good:
 * `const Toast = useToastContext()`
 * Bad:
 * `const {error} = useToastContext()`
 */
export const useToastContext = () => useProvideContext(ToastContext, 'Toast')
